import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Activity,
  AlertCircle,
  ArrowLeft,
  Bell,
  CalendarDays,
  Check,
  ChevronLeft,
  ChevronRight,
  CircleUserRound,
  Clock3,
  Copy,
  Edit3,
  ExternalLink,
  Globe2,
  HardDrive,
  Hash,
  LockKeyhole,
  Mail,
  Music2,
  RefreshCw,
  Save,
  Send,
  ShieldCheck,
  Smartphone,
  UserCheck,
  UserRoundCog,
  UserX,
  X,
} from "lucide-react";
import {
  AdminUserDevice,
  AdminUserNotification,
  AdminUserNotificationDevice,
  AdminUserRole,
  AdminUserSong,
} from "../../types/admin";
import { PaginationMeta } from "../../types/api";
import { getCurrentTokenAccessInfo } from "../../utils/jwt";
import styles from "./UserShared.module.css";
import {
  fetchAdminUserDevices,
  fetchAdminUserNotifications,
  fetchAdminUserSongs,
  fetchUser,
  fetchUserRoles,
  AdminUserRolesResponse,
  updateUser,
  updateUserRoles,
  updateUserStatus,
  User,
} from "./userApi";
import {
  AssignUserSongModal,
  SendUserNotificationModal,
} from "./UserActionModals";

type TabKey = "profile" | "devices" | "songs" | "notifications" | "roles";

const dateTimeFormatter = new Intl.DateTimeFormat("tr-TR", {
  dateStyle: "medium",
  timeStyle: "short",
});

function formatDateTime(value: unknown) {
  if (typeof value !== "string" || !value) return "—";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : dateTimeFormatter.format(date);
}

function humanizeKey(key: string) {
  return key
    .replace(/^_/, "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function displayValue(key: string, value: unknown): React.ReactNode {
  if (value === null || value === undefined || value === "") {
    return <span className={styles.emptyApiValue}>—</span>;
  }

  if (typeof value === "boolean") {
    return (
      <span
        className={`${styles.booleanValue} ${
          value ? styles.booleanTrue : styles.booleanFalse
        }`}
      >
        {value ? "Evet" : "Hayır"}
      </span>
    );
  }

  if (
    typeof value === "string" &&
    (key.endsWith("_at") || key.includes("login") || key.includes("date"))
  ) {
    return formatDateTime(value);
  }

  if (Array.isArray(value)) {
    if (value.length === 0) return <span className={styles.emptyApiValue}>Boş liste</span>;
    if (value.every((item) => typeof item !== "object")) {
      return (
        <div className={styles.apiValueChips}>
          {value.map((item, index) => (
            <span key={`${String(item)}-${index}`}>{String(item)}</span>
          ))}
        </div>
      );
    }
  }

  if (typeof value === "object") {
    return (
      <pre className={styles.apiJsonValue}>
        {JSON.stringify(value, null, 2)}
      </pre>
    );
  }

  return String(value);
}

function ApiFields({
  data,
  exclude = [],
}: {
  data: Record<string, unknown>;
  exclude?: string[];
}) {
  const entries = Object.entries(data).filter(([key]) => !exclude.includes(key));

  if (entries.length === 0) {
    return <p className={styles.detailEmptyText}>Ek API alanı bulunmuyor.</p>;
  }

  return (
    <div className={styles.apiFieldsGrid}>
      {entries.map(([key, value]) => (
        <div key={key} className={styles.apiField}>
          <span>{humanizeKey(key)}</span>
          <div>{displayValue(key, value)}</div>
        </div>
      ))}
    </div>
  );
}

function getInitials(user: User) {
  const source = user.name || user.email || user.user_id;
  return source
    .split(/[\s@._-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("");
}

export default function UserDetail() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const access = getCurrentTokenAccessInfo();
  const canManageUsers = Boolean(access?.canManageUsers);
  const canManageUserRoles = Boolean(access?.canManageUserRoles);
  const canSendNotifications = Boolean(access?.hasPanelAccess);
  const canAssignSongs = Boolean(access?.hasPanelAccess);

  const [user, setUser] = useState<User | null>(
    (location.state as { user?: User } | null)?.user || null
  );
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [sectionErrors, setSectionErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState<TabKey>("profile");

  const [devices, setDevices] = useState<AdminUserDevice[]>([]);
  const [notificationDevices, setNotificationDevices] = useState<
    AdminUserNotificationDevice[]
  >([]);
  const [songs, setSongs] = useState<AdminUserSong[]>([]);
  const [notifications, setNotifications] = useState<AdminUserNotification[]>([]);
  const [notificationMeta, setNotificationMeta] = useState<PaginationMeta | null>(null);
  const [notificationPage, setNotificationPage] = useState(1);
  const [roles, setRoles] = useState<AdminUserRole[]>([]);
  const [roleSummary, setRoleSummary] = useState<AdminUserRolesResponse | null>(
    null
  );

  const [name, setName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"active" | "disabled">("active");
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [songModalOpen, setSongModalOpen] = useState(false);

  const applyProfile = useCallback((profile: User) => {
    setUser(profile);
    setName(profile.name || "");
    setDisplayName(profile.display_name || "");
    setEmail(profile.email || "");
    setStatus(profile.status === "disabled" || profile.is_deleted ? "disabled" : "active");
  }, []);

  const loadAll = useCallback(
    async (userId: string, isRefresh = false) => {
      try {
        isRefresh ? setRefreshing(true) : setLoading(true);
        setError("");
        setSectionErrors({});

        const profile = await fetchUser(userId);
        applyProfile(profile);

        const [deviceResult, songResult, notificationResult, roleResult] =
          await Promise.all([
            fetchAdminUserDevices(userId)
              .then((data) => ({ data }))
              .catch((reason) => ({ error: reason })),
            fetchAdminUserSongs(userId)
              .then((data) => ({ data }))
              .catch((reason) => ({ error: reason })),
            fetchAdminUserNotifications(userId, 1, 50)
              .then((data) => ({ data }))
              .catch((reason) => ({ error: reason })),
            canManageUserRoles
              ? fetchUserRoles(userId)
                  .then((data) => ({ data }))
                  .catch((reason) => ({ error: reason }))
              : Promise.resolve({ data: [] as AdminUserRole[] }),
          ]);

        const nextErrors: Record<string, string> = {};

        if ("data" in deviceResult) {
          setDevices(deviceResult.data.devices);
          setNotificationDevices(deviceResult.data.notification_devices);
        }
        else nextErrors.devices = deviceResult.error?.message || "Cihazlar alınamadı.";

        if ("data" in songResult) setSongs(songResult.data);
        else nextErrors.songs = songResult.error?.message || "Şarkılar alınamadı.";

        if ("data" in notificationResult) {
          setNotifications(notificationResult.data.items);
          setNotificationMeta(notificationResult.data.meta);
          setNotificationPage(1);
        } else {
          nextErrors.notifications =
            notificationResult.error?.message || "Bildirimler alınamadı.";
        }

        if ("data" in roleResult) {
          const roleData = Array.isArray(roleResult.data)
            ? {
                user_id: userId,
                roles: roleResult.data,
                admin: false,
              }
            : roleResult.data;
          setRoleSummary(roleData);
          setRoles(roleData.roles);
          setSelectedRoles(
            roleData.roles.map((item) => item.role.toUpperCase())
          );
        } else {
          nextErrors.roles = roleResult.error?.message || "Roller alınamadı.";
        }

        setSectionErrors(nextErrors);
      } catch (err: any) {
        setError(err?.message || "Kullanıcı detayı yüklenemedi.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [applyProfile, canManageUserRoles]
  );

  useEffect(() => {
    if (id) loadAll(id);
  }, [id, loadAll]);

  const loadNotificationPage = async (nextPage: number) => {
    if (!id) return;
    try {
      setRefreshing(true);
      const result = await fetchAdminUserNotifications(id, nextPage, 50);
      setNotifications(result.items);
      setNotificationMeta(result.meta);
      setNotificationPage(nextPage);
      setSectionErrors((current) => {
        const next = { ...current };
        delete next.notifications;
        return next;
      });
    } catch (err: any) {
      setSectionErrors((current) => ({
        ...current,
        notifications: err?.message || "Bildirimler alınamadı.",
      }));
    } finally {
      setRefreshing(false);
    }
  };

  const onSaveProfile = async () => {
    if (!id || !user || !canManageUsers) return;
    try {
      setSaving(true);
      setError("");
      setMessage("");

      const updated = await updateUser(id, {
        name: name.trim(),
        display_name: displayName.trim() || undefined,
        email: email.trim(),
      });
      let tokenRefreshRequired = false;

      if (status !== (user.status === "disabled" || user.is_deleted ? "disabled" : "active")) {
        const result = await updateUserStatus(id, { status });
        tokenRefreshRequired = result.requires_token_refresh === true;
      }

      applyProfile({ ...updated, status, is_deleted: status === "disabled" });
      setMessage(
        tokenRefreshRequired
          ? "Profil ve durum güncellendi. Kullanıcının token yenilemesi gerekiyor."
          : "Kullanıcı profili güncellendi."
      );
    } catch (err: any) {
      setError(err?.message || "Kullanıcı güncellenemedi.");
    } finally {
      setSaving(false);
    }
  };

  const onUpdateRoles = async () => {
    if (!id || !canManageUserRoles) return;
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const result = await updateUserRoles(id, { roles: selectedRoles });
      setRoles(selectedRoles.map((role) => ({ role })));
      setRoleSummary((current) => ({
        user_id: current?.user_id || id,
        roles: selectedRoles.map((role) => ({ role })),
        admin: selectedRoles.includes("ADMIN"),
        requires_token_refresh: result.requires_token_refresh,
      }));
      setMessage(
        result.requires_token_refresh
          ? "Roller güncellendi. Kullanıcının token yenilemesi gerekiyor."
          : "Kullanıcı rolleri güncellendi."
      );
    } catch (err: any) {
      setError(err?.message || "Roller güncellenemedi.");
    } finally {
      setSaving(false);
    }
  };

  const toggleRole = (role: string) => {
    setSelectedRoles((current) =>
      current.includes(role)
        ? current.filter((item) => item !== role)
        : [...current, role]
    );
  };

  const copyUserId = async () => {
    if (!user) return;
    try {
      await navigator.clipboard.writeText(user.user_id);
      setMessage("Kullanıcı ID panoya kopyalandı.");
    } catch {
      setError("Kullanıcı ID kopyalanamadı.");
    }
  };

  const completeUserAction = (successMessage: string, tab: TabKey) => {
    setMessage(successMessage);
    setError("");
    setActiveTab(tab);
    if (id) loadAll(id, true);
  };

  const roleNames = useMemo(
    () => roles.map((item) => item.role.toUpperCase()),
    [roles]
  );

  const notificationTotal =
    typeof notificationMeta?.total === "number"
      ? notificationMeta.total
      : notifications.length;
  const notificationLimit =
    typeof notificationMeta?.limit === "number" ? notificationMeta.limit : 50;
  const notificationTotalPages =
    typeof notificationMeta?.total_pages === "number"
      ? notificationMeta.total_pages
      : Math.max(1, Math.ceil(notificationTotal / notificationLimit));

  const tabs: Array<{
    key: TabKey;
    label: string;
    count?: number;
    icon: React.ElementType;
  }> = [
    { key: "profile", label: "Profil", icon: CircleUserRound },
    {
      key: "devices",
      label: "Cihazlar",
      count: devices.length + notificationDevices.length,
      icon: Smartphone,
    },
    { key: "songs", label: "Şarkılar", count: songs.length, icon: Music2 },
    {
      key: "notifications",
      label: "Bildirimler",
      count: notificationTotal,
      icon: Bell,
    },
    ...(canManageUserRoles
      ? [{ key: "roles" as const, label: "Roller", count: roles.length, icon: ShieldCheck }]
      : []),
  ];

  if (loading) {
    return (
      <div className={styles.userDetailState}>
        <div className={styles.usersLoader} />
        <h2>Kullanıcı profili hazırlanıyor</h2>
        <p>Profil ve bağlantılı hesap verileri birlikte yükleniyor.</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className={`${styles.userDetailState} ${styles.usersErrorState}`}>
        <AlertCircle size={34} />
        <h2>Kullanıcı bulunamadı</h2>
        <p>{error || "Bu kullanıcıya ait profil bilgisi alınamadı."}</p>
        <button type="button" onClick={() => navigate("/users")}>
          <ArrowLeft size={16} /> Listeye dön
        </button>
      </div>
    );
  }

  return (
    <div className={styles.userDetailPage}>
      <button
        type="button"
        className={styles.detailBackButton}
        onClick={() => navigate("/users")}
      >
        <ArrowLeft size={16} />
        Kullanıcılara dön
      </button>

      <header className={styles.userProfileHero}>
        <div className={styles.profileIdentity}>
          <span className={styles.profileAvatar}>{getInitials(user)}</span>
          <div>
            <div className={styles.profileNameLine}>
              <h1>{user.name || "İsimsiz kullanıcı"}</h1>
              <span
                className={`${styles.userStatus} ${
                  status === "active" ? styles.statusActive : styles.statusDisabled
                }`}
              >
                {status}
              </span>
            </div>
            <p>
              <Mail size={15} />
              {user.email || "E-posta bilgisi yok"}
            </p>
            <button type="button" onClick={copyUserId}>
              <Hash size={13} />
              {user.user_id}
              <Copy size={13} />
            </button>
          </div>
        </div>

        <div className={styles.profileHeroActions}>
          <span
            className={`${styles.permissionBadge} ${
              canManageUsers ? styles.permissionAdmin : styles.permissionReadOnly
            }`}
          >
            {canManageUsers ? <ShieldCheck size={15} /> : <LockKeyhole size={15} />}
            {canManageUsers ? "Düzenlenebilir" : "Salt okunur"}
          </span>
          <button
            type="button"
            className={styles.detailRefreshButton}
            disabled={refreshing}
            onClick={() => id && loadAll(id, true)}
          >
            <RefreshCw
              size={16}
              className={refreshing ? styles.usersSpinning : undefined}
            />
            Yenile
          </button>
        </div>
      </header>

      {(error || message) && (
        <div
          className={`${styles.detailAlert} ${
            error ? styles.detailAlertError : styles.detailAlertSuccess
          }`}
          role="status"
        >
          {error ? <AlertCircle size={17} /> : <Check size={17} />}
          <span>{error || message}</span>
          <button
            type="button"
            aria-label="Mesajı kapat"
            onClick={() => {
              setError("");
              setMessage("");
            }}
          >
            <X size={15} />
          </button>
        </div>
      )}

      <section className={styles.userDetailStats}>
        <div>
          <span className={styles.statIconBlue}>
            <Music2 size={18} />
          </span>
          <p>Atanmış şarkı</p>
          <strong>{user.assigned_song_count ?? songs.length}</strong>
        </div>
        <div>
          <span className={styles.statIconGreen}>
            <Smartphone size={18} />
          </span>
          <p>Klasik cihaz</p>
          <strong>{user.device_count ?? devices.length}</strong>
        </div>
        <div>
          <span className={styles.statIconCyan}>
            <Bell size={18} />
          </span>
          <p>Push cihazı</p>
          <strong>{notificationDevices.length}</strong>
        </div>
        <div>
          <span className={styles.statIconOrange}>
            <Bell size={18} />
          </span>
          <p>Bildirim</p>
          <strong>{notificationTotal}</strong>
        </div>
        <div>
          <span className={styles.statIconCyan}>
            <ShieldCheck size={18} />
          </span>
          <p>Rol</p>
          <strong>{roles.length}</strong>
        </div>
      </section>

      {canSendNotifications && (
        <section className={styles.userQuickActions}>
          <div>
            <span>Hızlı işlemler</span>
            <h2>Bu kullanıcı için işlem yap</h2>
          </div>
          <div className={styles.userQuickActionButtons}>
            <div className={styles.userUnsupportedAction}>
              <span className={styles.quickDeviceIcon}>
                <Smartphone size={17} />
              </span>
              <div>
                <strong>Cihazlar salt okunur</strong>
                <small>Kullanıcı kendi hesabından eşleştirir</small>
              </div>
              <LockKeyhole size={14} />
            </div>
            <button
              type="button"
              onClick={() => setSongModalOpen(true)}
              disabled={!canAssignSongs}
            >
              <span className={styles.quickSongIcon}>
                <Music2 size={17} />
              </span>
              <div>
                <strong>V2 şarkı ata</strong>
                <small>Katalogdan kullanıcıya ekle</small>
              </div>
              <ExternalLink size={14} />
            </button>
            <button
              type="button"
              onClick={() => setNotificationModalOpen(true)}
            >
              <span className={styles.quickNotificationIcon}>
                <Bell size={17} />
              </span>
              <div>
                <strong>Bildirim gönder</strong>
                <small>Tekil mesaj oluştur</small>
              </div>
              <Send size={15} />
            </button>
          </div>
        </section>
      )}

      <nav className={styles.userDetailTabs} aria-label="Kullanıcı detay bölümleri">
        {tabs.map(({ key, label, count, icon: Icon }) => (
          <button
            type="button"
            key={key}
            className={activeTab === key ? styles.detailTabActive : undefined}
            onClick={() => setActiveTab(key)}
          >
            <Icon size={16} />
            {label}
            {typeof count === "number" && <span>{count}</span>}
            {sectionErrors[key] && <i aria-label="Bu bölüm yüklenemedi" />}
          </button>
        ))}
      </nav>

      {activeTab === "profile" && (
        <div className={styles.detailTwoColumn}>
          <section className={styles.detailPanel}>
            <div className={styles.detailPanelHeader}>
              <div>
                <span>Hesap</span>
                <h2>Profil bilgileri</h2>
              </div>
              <Edit3 size={18} />
            </div>
            <div className={styles.profileFormGrid}>
              <label>
                <span>
                  <CircleUserRound size={14} /> İsim
                </span>
                <input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  disabled={!canManageUsers}
                />
              </label>
              <label>
                <span>
                  <CircleUserRound size={14} /> Görünen ad
                </span>
                <input
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                  disabled={!canManageUsers}
                />
              </label>
              <label>
                <span>
                  <Mail size={14} /> E-posta
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  disabled={!canManageUsers}
                />
              </label>
              <label className={styles.fullFormField}>
                <span>
                  <UserRoundCog size={14} /> Hesap durumu
                </span>
                <select
                  value={status}
                  onChange={(event) =>
                    setStatus(event.target.value as "active" | "disabled")
                  }
                  disabled={!canManageUsers}
                >
                  <option value="active">Aktif</option>
                  <option value="disabled">Devre dışı</option>
                </select>
              </label>
            </div>
            {canManageUsers && (
              <div className={styles.detailPanelFooter}>
                <button type="button" onClick={onSaveProfile} disabled={saving}>
                  <Save size={16} />
                  {saving ? "Kaydediliyor" : "Profili kaydet"}
                </button>
              </div>
            )}
          </section>

          <section className={styles.detailPanel}>
            <div className={styles.detailPanelHeader}>
              <div>
                <span>Zaman çizelgesi</span>
                <h2>Hesap aktivitesi</h2>
              </div>
              <Clock3 size={18} />
            </div>
            <div className={styles.accountTimeline}>
              <div>
                <span>
                  <CalendarDays size={16} />
                </span>
                <div>
                  <p>Hesap oluşturuldu</p>
                  <strong>{formatDateTime(user.created_at)}</strong>
                </div>
              </div>
              <div>
                <span>
                  <Activity size={16} />
                </span>
                <div>
                  <p>Hesap durumu</p>
                  <strong>{status === "active" ? "Aktif" : "Devre dışı"}</strong>
                </div>
              </div>
              <div>
                <span>
                  <UserCheck size={16} />
                </span>
                <div>
                  <p>Son giriş</p>
                  <strong>{formatDateTime(user.last_login)}</strong>
                </div>
              </div>
            </div>
          </section>

          <section className={`${styles.detailPanel} ${styles.fullDetailPanel}`}>
            <div className={styles.detailPanelHeader}>
              <div>
                <span>Ham veri görünümü</span>
                <h2>API'den gelen tüm profil alanları</h2>
              </div>
              <Globe2 size={18} />
            </div>
            <ApiFields data={user} />
          </section>
        </div>
      )}

      {activeTab === "devices" && (
        <section className={styles.detailPanel}>
          <div className={styles.detailPanelHeader}>
            <div>
              <span>Donanım</span>
              <h2>Kayıtlı cihazlar</h2>
            </div>
            <Smartphone size={18} />
          </div>
          {sectionErrors.devices ? (
            <div className={styles.sectionError}>
              <AlertCircle size={18} /> {sectionErrors.devices}
            </div>
          ) : devices.length === 0 && notificationDevices.length === 0 ? (
            <div className={styles.detailEmpty}>
              <HardDrive size={30} />
              <h3>Kayıtlı cihaz yok</h3>
              <p>Bu kullanıcıyla eşleştirilmiş bir cihaz bulunmuyor.</p>
            </div>
          ) : (
            <div className={styles.deviceSections}>
              {devices.length > 0 && (
                <div>
                  <h3 className={styles.resourceSectionTitle}>
                    <HardDrive size={15} /> Klasik cihazlar ({devices.length})
                  </h3>
                  <div className={styles.resourceGrid}>
                    {devices.map((device) => (
                      <article key={device.device_id} className={styles.resourceCard}>
                        <div className={styles.resourceCardHeader}>
                          <span className={styles.resourceIcon}>
                            <Smartphone size={19} />
                          </span>
                          <div>
                            <h3>{device.model || "Bilinmeyen cihaz"}</h3>
                            <p>{device.serial_number || device.device_id}</p>
                          </div>
                          <span
                            className={`${styles.userStatus} ${
                              device.is_active
                                ? styles.statusActive
                                : styles.statusDisabled
                            }`}
                          >
                            {device.is_active ? "aktif" : "pasif"}
                          </span>
                        </div>
                        <ApiFields data={device} />
                      </article>
                    ))}
                  </div>
                </div>
              )}

              {notificationDevices.length > 0 && (
                <div>
                  <h3 className={styles.resourceSectionTitle}>
                    <Bell size={15} /> Push bildirim cihazları (
                    {notificationDevices.length})
                  </h3>
                  <div className={styles.resourceGrid}>
                    {notificationDevices.map((device) => (
                      <article
                        key={device.id || device.device_id}
                        className={styles.resourceCard}
                      >
                        <div className={styles.resourceCardHeader}>
                          <span className={styles.resourceIconViolet}>
                            <Bell size={19} />
                          </span>
                          <div>
                            <h3>{device.platform}</h3>
                            <p>{device.device_id}</p>
                          </div>
                          <span
                            className={`${styles.userStatus} ${
                              device.is_active
                                ? styles.statusActive
                                : styles.statusDisabled
                            }`}
                          >
                            {device.is_active ? "aktif" : "pasif"}
                          </span>
                        </div>
                        <ApiFields data={device} />
                      </article>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {activeTab === "songs" && (
        <section className={styles.detailPanel}>
          <div className={styles.detailPanelHeader}>
            <div>
              <span>İçerik kütüphanesi</span>
              <h2>Atanmış şarkılar</h2>
            </div>
            <Music2 size={18} />
          </div>
          {sectionErrors.songs ? (
            <div className={styles.sectionError}>
              <AlertCircle size={18} /> {sectionErrors.songs}
            </div>
          ) : songs.length === 0 ? (
            <div className={styles.detailEmpty}>
              <Music2 size={30} />
              <h3>Atanmış şarkı yok</h3>
              <p>Bu kullanıcının şarkı listesi henüz boş.</p>
            </div>
          ) : (
            <div className={styles.resourceGrid}>
              {songs.map((song) => (
                <article key={song.songv2_id} className={styles.resourceCard}>
                  <div className={styles.resourceCardHeader}>
                    <span className={styles.resourceIconViolet}>
                      <Music2 size={19} />
                    </span>
                    <div>
                      <h3>{song.title || "İsimsiz şarkı"}</h3>
                      <p>{song.artist || song.songv2_id}</p>
                    </div>
                    <button
                      type="button"
                      aria-label="Şarkı detayına git"
                      onClick={() => navigate(`/songs/${song.songv2_id}`)}
                    >
                      <ExternalLink size={16} />
                    </button>
                  </div>
                  <ApiFields data={song} />
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      {activeTab === "notifications" && (
        <section className={styles.detailPanel}>
          <div className={styles.detailPanelHeader}>
            <div>
              <span>İletişim geçmişi</span>
              <h2>Kullanıcı bildirimleri</h2>
            </div>
            <Bell size={18} />
          </div>
          {sectionErrors.notifications ? (
            <div className={styles.sectionError}>
              <AlertCircle size={18} /> {sectionErrors.notifications}
            </div>
          ) : notifications.length === 0 ? (
            <div className={styles.detailEmpty}>
              <Bell size={30} />
              <h3>Bildirim bulunamadı</h3>
              <p>Bu kullanıcıya ait kayıtlı bildirim yok.</p>
            </div>
          ) : (
            <>
              <div className={styles.notificationList}>
                {notifications.map((notification) => (
                  <article
                    key={notification.id || notification.notification_id}
                    className={styles.notificationCard}
                  >
                    <span className={styles.notificationIcon}>
                      <Bell size={17} />
                    </span>
                    <div>
                      <div className={styles.notificationTitleLine}>
                        <h3>{notification.title || "Başlıksız bildirim"}</h3>
                        {notification.push_status && (
                          <span>{notification.push_status}</span>
                        )}
                      </div>
                      <p>{notification.body || "Bildirim içeriği yok."}</p>
                      <ApiFields data={notification} exclude={["title", "body"]} />
                    </div>
                  </article>
                ))}
              </div>
              {notificationTotalPages > 1 && (
                <div className={styles.detailPagination}>
                  <button
                    type="button"
                    disabled={notificationPage <= 1 || refreshing}
                    onClick={() => loadNotificationPage(notificationPage - 1)}
                  >
                    <ChevronLeft size={16} /> Önceki
                  </button>
                  <span>
                    Sayfa {notificationPage} / {notificationTotalPages}
                  </span>
                  <button
                    type="button"
                    disabled={
                      notificationPage >= notificationTotalPages || refreshing
                    }
                    onClick={() => loadNotificationPage(notificationPage + 1)}
                  >
                    Sonraki <ChevronRight size={16} />
                  </button>
                </div>
              )}
            </>
          )}
        </section>
      )}

      {activeTab === "roles" && (
        <div className={styles.detailTwoColumn}>
          <section className={styles.detailPanel}>
            <div className={styles.detailPanelHeader}>
              <div>
                <span>Erişim kontrolü</span>
                <h2>Rol yönetimi</h2>
              </div>
              <ShieldCheck size={18} />
            </div>
            {sectionErrors.roles ? (
              <div className={styles.sectionError}>
                <AlertCircle size={18} /> {sectionErrors.roles}
              </div>
            ) : (
              <>
                <div className={styles.roleSelector}>
                  <button
                    type="button"
                    className={
                      selectedRoles.includes("ADMIN")
                        ? styles.roleOptionSelected
                        : undefined
                    }
                    onClick={() => canManageUserRoles && toggleRole("ADMIN")}
                    disabled={!canManageUserRoles || saving}
                  >
                    <span>
                      <ShieldCheck size={20} />
                    </span>
                    <div>
                      <strong>Admin</strong>
                      <p>Kullanıcı, rol ve durum yönetimi dahil tam panel erişimi.</p>
                    </div>
                    <i>{selectedRoles.includes("ADMIN") && <Check size={14} />}</i>
                  </button>
                  <button
                    type="button"
                    className={
                      selectedRoles.includes("CONTENT_MANAGER")
                        ? styles.roleOptionSelected
                        : undefined
                    }
                    onClick={() =>
                      canManageUserRoles && toggleRole("CONTENT_MANAGER")
                    }
                    disabled={!canManageUserRoles || saving}
                  >
                    <span>
                      <Edit3 size={20} />
                    </span>
                    <div>
                      <strong>Content Manager</strong>
                      <p>İçerik araçları ve kullanıcılar için salt-okunur erişim.</p>
                    </div>
                    <i>
                      {selectedRoles.includes("CONTENT_MANAGER") && (
                        <Check size={14} />
                      )}
                    </i>
                  </button>
                </div>
                {canManageUserRoles ? (
                  <div className={styles.detailPanelFooter}>
                    <button type="button" onClick={onUpdateRoles} disabled={saving}>
                      <Save size={16} />
                      {saving ? "Kaydediliyor" : "Rolleri kaydet"}
                    </button>
                  </div>
                ) : (
                  <div className={styles.readOnlyNotice}>
                    <LockKeyhole size={17} />
                    Bu hesapta rol değiştirme yetkisi yok. Roller salt-okunur
                    gösteriliyor.
                  </div>
                )}
              </>
            )}
          </section>

          <section className={styles.detailPanel}>
            <div className={styles.detailPanelHeader}>
              <div>
                <span>Aktif roller</span>
                <h2>Yetki özeti</h2>
              </div>
              <UserRoundCog size={18} />
            </div>
            {roleNames.length === 0 ? (
              <div className={styles.detailEmpty}>
                <UserX size={28} />
                <h3>Atanmış rol yok</h3>
                <p>Bu kullanıcı panel erişimine sahip olmayabilir.</p>
              </div>
            ) : (
              <div className={styles.roleSummaryList}>
                {roleNames.map((role) => (
                  <div key={role}>
                    <span>
                      <ShieldCheck size={16} />
                    </span>
                    <strong>{role}</strong>
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className={`${styles.detailPanel} ${styles.fullDetailPanel}`}>
            <div className={styles.detailPanelHeader}>
              <div>
                <span>Ham veri görünümü</span>
                <h2>API'den gelen tüm rol alanları</h2>
              </div>
              <Globe2 size={18} />
            </div>
            {!roleSummary ? (
              <p className={styles.detailEmptyText}>Rol kaydı bulunmuyor.</p>
            ) : (
              <ApiFields
                data={{
                  user_id: roleSummary.user_id,
                  roles: roleSummary.roles.map((item) => item.role),
                  admin: roleSummary.admin,
                  requires_token_refresh:
                    roleSummary.requires_token_refresh ?? null,
                }}
              />
            )}
          </section>
        </div>
      )}

      <SendUserNotificationModal
        isOpen={notificationModalOpen}
        onClose={() => setNotificationModalOpen(false)}
        onComplete={(successMessage) =>
          completeUserAction(successMessage, "notifications")
        }
        userId={user.user_id}
      />
      <AssignUserSongModal
        isOpen={songModalOpen}
        onClose={() => setSongModalOpen(false)}
        onComplete={(successMessage) =>
          completeUserAction(successMessage, "songs")
        }
        userId={user.user_id}
        assignedSongIds={songs.map((song) => song.songv2_id)}
      />
    </div>
  );
}
